import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Avatar, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Drawer, FormControlLabel, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { api } from "../api";
import { toDisplayImageUrl } from "../utils/imageUrl";
import { canAccessSiteOperations, useCapabilities } from "../contexts/CapabilitiesContext";
import usePageTitle from "../hooks/usePageTitle";

export const EDIT_FIELDS = ["name", "brand", "category", "year_released", "set_name", "series_collection", "number", "language", "rarity", "edition_variant", "authentication", "condition_grade", "item_condition", "image_url", "ref_image_url", "content_status"];
const PERMISSIONS = ["catalog_edit", "user_privacy_manage"];
const PAGE_SIZE = 25;
const fieldLabel = (field) => field.replaceAll("_", " ");
const yesNo = (value) => Number(value) === 1 || value === true ? "Yes" : "No";
const dateText = (value) => value ? new Date(value).toLocaleString() : "—";
const auditSummary = (value) => {
  if (value == null) return "None";
  const object = typeof value === "object" ? value : null;
  if (!object) return String(value).slice(0, 100);
  const entries = Object.entries(object).slice(0, 4).map(([key, entry]) => `${fieldLabel(key)}: ${String(entry).slice(0, 40)}`);
  return entries.join(" · ") || "None";
};
export const getOperationsSections = (isAdmin) => isAdmin
  ? ["recent", "search", "users", "changes", "access"]
  : ["recent", "search", "changes"];

function ItemRow({ row, onEdit }) {
  return <Card variant="outlined" sx={{ mb: 1 }}><CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
      <Avatar variant="rounded" src={toDisplayImageUrl(row.image_url)} alt="" sx={{ width: 52, height: 52, bgcolor: "grey.100" }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800} noWrap>#{row.item_id} {row.name}</Typography>
        <Typography variant="body2" color="text.secondary">{row.category || "—"} · {row.set_name || row.series_collection || "—"} · {row.number || "—"}</Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
          {row.content_status && <Chip size="small" label={row.content_status} />}
          {row.created_at && <Chip size="small" variant="outlined" label={dateText(row.created_at)} />}
        </Stack>
      </Box>
      <Button variant="outlined" onClick={() => onEdit(row.item_id)} sx={{ minHeight: 44, flexShrink: 0 }}>Edit</Button>
    </Box>
  </CardContent></Card>;
}

function UserCard({ user, onOpen }) {
  return <Card variant="outlined" sx={{ mb: 1, cursor: "pointer" }} onClick={() => onOpen(user.user_id)}>
    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Avatar src={toDisplayImageUrl(user.profile_picture)} alt="" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={800}>#{user.user_id} {user.name || "Unnamed user"}</Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{user.email || "—"}</Typography>
          <Typography variant="caption" color="text.secondary">Created: {dateText(user.created_at)}</Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip size="small" label={`Admin: ${yesNo(user.is_admin)}`} />
            <Chip size="small" label={`Private: ${yesNo(user.is_private)}`} />
            <Chip size="small" label={`Collection prices: ${yesNo(user.show_collection_prices)}`} />
            <Chip size="small" label={`Sitewide prices: ${yesNo(user.show_prices_sitewide)}`} />
            <Chip size="small" label={`Items/page: ${user.items_per_page_preference ?? "—"}`} />
          </Stack>
        </Box>
      </Box>
    </CardContent>
  </Card>;
}

export default function AdminMobilePage() {
  usePageTitle("Site Operations");
  const { capabilities, capabilitiesLoaded } = useCapabilities();
  const isAdmin = Boolean(capabilities?.is_admin);
  const tabs = useMemo(() => getOperationsSections(isAdmin), [isAdmin]);
  const [tab, setTab] = useState("recent");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [item, setItem] = useState(null);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [userOffset, setUserOffset] = useState(0);
  const [userTotal, setUserTotal] = useState(0);
  const [usersInitialized, setUsersInitialized] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [accessQuery, setAccessQuery] = useState("");
  const [accessUsers, setAccessUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recentLoading, setRecentLoading] = useState(false);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try { const { data } = await api.get("/api/admin/mobile/items/recent", { params: { limit: PAGE_SIZE } }); setRecentItems(data.items || []); }
    catch { setError("Recent items could not be loaded."); }
    finally { setRecentLoading(false); }
  }, []);
  const loadAudit = useCallback(() => api.get("/api/admin/mobile/audit?limit=25").then(({ data }) => setAudit(data.audit || [])).catch(() => setError("Recent changes could not be loaded.")), []);

  useEffect(() => {
    if (!capabilitiesLoaded || !canAccessSiteOperations(capabilities)) return;
    loadRecent();
    loadAudit();
  }, [capabilities, capabilitiesLoaded, loadAudit, loadRecent]);

  const searchItems = async () => {
    setBusy(true); setError("");
    try { const { data } = await api.get("/api/admin/mobile/items", { params: { q: query } }); setItems(data.items || []); }
    catch { setError("Item search failed."); } finally { setBusy(false); }
  };
  const openItem = async (id) => {
    setBusy(true); setError("");
    try { const { data } = await api.get(`/api/admin/mobile/items/${id}`); setItem(data.item); setTab("search"); }
    catch { setError("Item could not be loaded."); } finally { setBusy(false); }
  };
  const saveItem = async () => {
    if (!window.confirm("Apply these approved catalog changes?")) return;
    setBusy(true); setError("");
    try {
      const body = Object.fromEntries(EDIT_FIELDS.map((field) => [field, item[field] ?? null]));
      const { data } = await api.patch(`/api/admin/mobile/items/${item.item_id}`, body);
      setItem(data.item); setMessage("Item changes saved."); loadAudit(); loadRecent();
    } catch (e) { setError(e.response?.data?.error || "Item update failed."); } finally { setBusy(false); }
  };
  const loadUsers = useCallback(async (offset = userOffset, q = userQuery) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const { data } = await api.get("/api/admin/mobile/users", { params: { limit: PAGE_SIZE, offset, q } });
      setUsers(data.users || []); setUserTotal(Number(data.total || 0)); setUserOffset(Number(data.offset || 0));
    } catch { setError("Users could not be loaded."); } finally { setBusy(false); }
  }, [isAdmin, userOffset, userQuery]);
  useEffect(() => {
    if (tab === "users" && isAdmin && !usersInitialized) {
      setUsersInitialized(true);
      loadUsers(0, "");
    }
  }, [isAdmin, loadUsers, tab, usersInitialized]);
  const openUser = async (userId) => {
    if (!isAdmin) return;
    try { const { data } = await api.get(`/api/admin/mobile/users/${userId}`); setSelectedUser(data); }
    catch { setError("User details could not be loaded."); }
  };
  const searchAccess = async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/api/admin/mobile/users", { params: { limit: PAGE_SIZE, offset: 0, q: accessQuery } }); setAccessUsers(data.users || []); }
    catch { setError("User search failed."); }
  };
  const changePermission = async (user, permission, granted) => {
    if (!isAdmin || !window.confirm(`${granted ? "Grant" : "Revoke"} ${permission} for ${user.name}?`)) return;
    try {
      const { data } = await api.patch(`/api/admin/mobile/users/${user.user_id}/permissions/${permission}`, { granted });
      setAccessUsers((list) => list.map((entry) => entry.user_id === user.user_id ? { ...entry, permissions: data.permissions } : entry));
      if (selectedUser?.user?.user_id === user.user_id) setSelectedUser((current) => ({ ...current, permissions: data.permissions }));
      setMessage("Access updated."); loadAudit();
    } catch { setError("Access update failed."); }
  };

  if (!capabilitiesLoaded) return null;
  if (!canAccessSiteOperations(capabilities)) return <Box sx={{ p: 3 }}><Alert severity="error">Access denied.</Alert></Box>;

  return <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 900, mx: "auto", pb: 8 }}>
    <Typography variant="h4" fontWeight={800}>Site Operations</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>Manage approved catalog information and review recent changes.</Typography>
    {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}
    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
    <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
      {tabs.map((key) => <Tab key={key} value={key} label={{ recent: "Recent Items", search: "Search Items", users: "Users", changes: "Recent Changes", access: "Access Management" }[key]} />)}
    </Tabs>

    {tab === "recent" && <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="h6">Newest catalog items</Typography>
        <Button onClick={loadRecent} disabled={recentLoading} sx={{ minHeight: 44 }}>{recentLoading ? <CircularProgress size={20} /> : "Refresh"}</Button>
      </Box>
      {recentItems.map((row) => <ItemRow key={row.item_id} row={row} onEdit={openItem} />)}
    </Box>}

    {tab === "search" && <Box>
      <Card sx={{ mb: 2 }}><CardContent><Typography variant="h6">Search catalog</Typography><Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}><TextField fullWidth label="Item ID or name" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchItems()} /><Button variant="contained" onClick={searchItems} disabled={busy} sx={{ minHeight: 48 }}>Search</Button></Stack>{items.map((row) => <ItemRow key={row.item_id} row={row} onEdit={openItem} />)}</CardContent></Card>
      {item && <Card><CardContent><Typography variant="h6">Edit item #{item.item_id}</Typography><Box sx={{ display: "grid", gap: 2, mt: 2 }}>{EDIT_FIELDS.map((field) => <TextField key={field} label={fieldLabel(field)} value={item[field] ?? ""} onChange={(e) => setItem({ ...item, [field]: e.target.value })} fullWidth select={field === "content_status"} SelectProps={field === "content_status" ? { native: true } : undefined}>{field === "content_status" ? ["approved", "needs_review", "rejected"].map((value) => <option key={value} value={value}>{value}</option>) : null}</TextField>)}</Box><Button fullWidth size="large" variant="contained" sx={{ mt: 2, minHeight: 52 }} disabled={busy} onClick={saveItem}>Confirm and save</Button></CardContent></Card>}
    </Box>}

    {tab === "users" && isAdmin && <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}><TextField fullWidth label="Search ID, email, or name" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers(0, userQuery)} /><Button variant="contained" onClick={() => loadUsers(0, userQuery)} sx={{ minHeight: 48 }}>Search</Button></Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{userTotal} result{userTotal === 1 ? "" : "s"}</Typography>
      {users.map((user) => <UserCard key={user.user_id} user={user} onOpen={openUser} />)}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}><Button disabled={busy || userOffset === 0} onClick={() => loadUsers(Math.max(0, userOffset - PAGE_SIZE), userQuery)}>Previous</Button><Button disabled={busy || userOffset + PAGE_SIZE >= userTotal} onClick={() => loadUsers(userOffset + PAGE_SIZE, userQuery)}>Next</Button></Box>
    </Box>}

    {tab === "changes" && <Box>{audit.map((entry) => <Card key={entry.audit_id} variant="outlined" sx={{ mb: 1 }}><CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}><Typography fontWeight={800}>{entry.action} · {entry.target_type} #{entry.target_id}</Typography><Typography variant="caption">Actor #{entry.actor_user_id} · {dateText(entry.created_at)}</Typography><Typography variant="body2" sx={{ mt: 1 }}>Before: {auditSummary(entry.before_json)}</Typography><Typography variant="body2">After: {auditSummary(entry.after_json)}</Typography>{isAdmin && <Box component="details" sx={{ mt: 1 }}><Box component="summary" sx={{ cursor: "pointer" }}>Inspect safe details</Box><Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12 }}>{JSON.stringify({ before: entry.before_json, after: entry.after_json }, null, 2)}</Box></Box>}</CardContent></Card>)}</Box>}

    {tab === "access" && isAdmin && <Box><Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}><TextField fullWidth label="Search users" value={accessQuery} onChange={(e) => setAccessQuery(e.target.value)} /><Button variant="contained" onClick={searchAccess} sx={{ minHeight: 48 }}>Search</Button></Stack>{accessUsers.map((user) => <Card key={user.user_id} variant="outlined" sx={{ mb: 1 }}><CardContent><Typography fontWeight={700}>{user.name} (#{user.user_id})</Typography>{PERMISSIONS.map((permission) => <FormControlLabel key={permission} control={<Checkbox checked={user.permissions.includes(permission)} onChange={(e) => changePermission(user, permission, e.target.checked)} />} label={permission} />)}</CardContent></Card>)}</Box>}

    <Drawer anchor="right" open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)} PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, p: 2 } }}>
      {selectedUser && <Box><Typography variant="h5" fontWeight={800}>{selectedUser.user.name || "User"}</Typography><Typography color="text.secondary">#{selectedUser.user.user_id} · {selectedUser.user.email}</Typography><Avatar src={toDisplayImageUrl(selectedUser.user.profile_picture)} sx={{ width: 72, height: 72, my: 2 }} /><Stack spacing={1}><Typography>Created: {dateText(selectedUser.user.created_at)}</Typography><Typography>Admin: {yesNo(selectedUser.user.is_admin)}</Typography><Typography>Private: {yesNo(selectedUser.user.is_private)}</Typography><Typography>Collection prices visible: {yesNo(selectedUser.user.show_collection_prices)}</Typography><Typography>Sitewide prices visible: {yesNo(selectedUser.user.show_prices_sitewide)}</Typography><Typography>Items per page: {selectedUser.user.items_per_page_preference ?? "—"}</Typography><Typography>Permissions: {selectedUser.permissions.join(", ") || "None"}</Typography></Stack><Typography variant="h6" sx={{ mt: 3 }}>Recent permission changes</Typography>{selectedUser.audit.map((entry) => <Typography key={entry.audit_id} variant="body2" sx={{ py: 0.5 }}>{entry.action} · {dateText(entry.created_at)}</Typography>)}<Button fullWidth onClick={() => setSelectedUser(null)} sx={{ mt: 3, minHeight: 48 }}>Close</Button></Box>}
    </Drawer>
  </Box>;
}
